import React, { useEffect, useMemo, useState } from 'react';
import { 
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  TextField,
  Grid,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon,
  MonetizationOn as MoneyIcon,
  AccountBalanceWallet as WalletIcon,
  LocalOffer as CouponIcon,
  People as UsersIcon,
  ShoppingCart as OrdersIcon,
  CurrencyBitcoin as CryptoIcon,
  Inventory as ProductsIcon,
  Dashboard as DashboardIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
// Note: Temporarily avoid x-date-pickers to resolve module mismatch. Using native date inputs.
import * as XLSX from 'xlsx';
import api, { meAPI, dashboardAPI } from '../Api';
import { useAuth } from '../context/AuthContext';
import { alpha, useTheme } from '@mui/material/styles';

const Home = () => {
  const { user, isAuth } = useAuth();
  const theme = useTheme();
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [merchantId, setMerchantId] = useState('');
  const [startDate, setStartDate] = useState(() => {
    // Находим понедельник текущей недели
    const today = dayjs();
    const monday = today.startOf('week').add(1, 'day'); // dayjs считает воскресенье началом недели, поэтому +1 день
    return monday.format('YYYY-MM-DD');
  });
  const [endDate, setEndDate] = useState(() => {
    // Находим воскресенье текущей недели
    const today = dayjs();
    const sunday = today.endOf('week').add(1, 'day'); // dayjs считает воскресенье началом недели, поэтому +1 день
    return sunday.format('YYYY-MM-DD');
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState(0); // 0-overview, 1-salaries
  const [salariesLoading, setSalariesLoading] = useState(false);
  const [salaries, setSalaries] = useState(null);
  // Courier detail dialog
  const [courierOpen, setCourierOpen] = useState(false);
  const [courierLoading, setCourierLoading] = useState(false);
  const [courier, setCourier] = useState(null);
  const [courierId, setCourierId] = useState(null);
  const [showNotFound, setShowNotFound] = useState(false);
  // Expanded rows in salaries
  const [expanded, setExpanded] = useState(new Set());
  // Additional dashboard stats
  const [totalsLoading, setTotalsLoading] = useState(false);
  const [totals, setTotals] = useState({ totalSum: 0, totalBaseSum: 0, totalCuponSum: 0, ordersCount: 0, usersCount: 0 });
  const [cryptoLoading, setCryptoLoading] = useState(false);
  const [cryptoStats, setCryptoStats] = useState({ rows: [], totalBase: 0, count: 0 });
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsStats, setProductsStats] = useState([]);
  // Total payout across all merchants
  const [totalPayoutLoading, setTotalPayoutLoading] = useState(false);
  const [totalPayout, setTotalPayout] = useState({ totalPayout: 0, merchantsCount: 0, merchants: [] });

  const formatCurrency = (amount) => {
    const n = Number(amount || 0);
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 2 }).format(n);
  };

  // Helper: pick first non-empty value among aliases
  const pickFirst = (...vals) => {
    for (const v of vals) {
      if (v !== undefined && v !== null && String(v).trim() !== '') return v;
    }
    return '-';
  };

  // Export salaries to XLSX
  const exportSalariesXlsx = () => {
    if (!salaries?.couriers) return;
    const rows = salaries.couriers.map((c, idx) => ({
      '№': idx + 1,
      'Курьер': c.courierName || c.courierTelegram || c.courierId,
      'Telegram': c.courierTelegram || '-',
      'Успешных заказов': c.successfulOrders,
      'Ненаходы': c.notFoundOrders,
      'Всего заказов': c.totalOrders,
      'Зарплата (руб)': c.totalSalary,
      'Штрафы из фасовки (руб)': c.packagingPenalty,
      'Дополнительные штрафы (руб)': c.additionalPenalty,
      'Общие штрафы (руб)': c.totalPenalty,
      'Итоговая зарплата (руб)': c.finalSalary,
      'Выручка (руб)': c.totalRevenue,
      '% ЗП от выручки': typeof c.salaryPercentage === 'number' ? Number(c.salaryPercentage.toFixed(2)) : ''
    }));

    rows.push({});
    rows.push({
      'Курьер': 'ИТОГО:',
      'Успешных заказов': salaries.couriers.reduce((s, c) => s + (c.successfulOrders || 0), 0),
      'Ненаходы': salaries.couriers.reduce((s, c) => s + (c.notFoundOrders || 0), 0),
      'Всего заказов': salaries.couriers.reduce((s, c) => s + (c.totalOrders || 0), 0),
      'Зарплата (руб)': salaries.totalStats?.totalSalarySum ?? '',
      'Дополнительные штрафы (руб)': salaries.couriers.reduce((s, c) => s + (c.additionalPenalty || 0), 0),
      'Общие штрафы (руб)': salaries.totalStats?.totalPenaltySum ?? '',
      'Итоговая зарплата (руб)': salaries.totalStats?.totalFinalSalarySum ?? '',
      'Выручка (руб)': salaries.totalStats?.totalRevenueSum ?? '',
      '% ЗП от выручки': typeof salaries.totalStats?.avgSalaryPercentage === 'number' ? Number(salaries.totalStats.avgSalaryPercentage.toFixed(2)) : ''
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 5 }, { wch: 24 }, { wch: 18 }, { wch: 16 }, { wch: 12 }, { wch: 16 },
      { wch: 18 }, { wch: 24 }, { wch: 24 }, { wch: 18 }, { wch: 20 }, { wch: 16 }, { wch: 16 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Зарплаты');
    const period = `${dayjs(startDate).format('DD.MM.YYYY')}-${dayjs(endDate).format('DD.MM.YYYY')}`;
    const fileName = `Зарплаты_курьеров_${period}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const canAccess = useMemo(() => {
    if (!user) return null; // неизвестно пока
    const r = (user?.role || '').toLowerCase();
    return r === 'admin' || r === 'ownershop';
  }, [user]);

  useEffect(() => {
    if (!canAccess) return;
    const load = async () => {
      setLoadingMerchants(true);
      try {
        const resp = await meAPI.getMyMerchants();
        const list = resp?.data?.data || [];
        setMerchants(list);
        if (list.length > 0) setMerchantId(list[0]._id || list[0].id || '');
      } catch (e) {
        // noop UI-friendly
      }
      setLoadingMerchants(false);
    };

    load();
  }, [canAccess]);

  // Load courier stats/salaries on filters or tab change
  useEffect(() => {
    if (!canAccess || !merchantId) return;
    if (tab === 0) {
      reloadStats();
    } else if (tab === 1) {
      reloadSalaries();
    }
  }, [canAccess, merchantId, startDate, endDate, tab]);

  // Load extra stats (totals, crypto, products)
  useEffect(() => {
    if (!canAccess || !merchantId) return;
    // Send dates as YYYY-MM-DD, backend will handle full day boundaries (00:00 to 23:59)
    const params = { merchant_id: merchantId, startDate, endDate };
    const loadStats = async () => {
      setTotalsLoading(true);
      setCryptoLoading(true);
      setProductsLoading(true);
      try {
        console.log('[Home] Load dashboard stats with params', params);
        const [t, c, p] = await Promise.all([
          api.get('/dashboard/stats', { params }),
          api.get('/dashboard/stats/crypto', { params }),
          api.get('/dashboard/stats/products-stats', { params }),
        ]);
        console.log('[Home] totals resp', t?.data);
        console.log('[Home] crypto resp', c?.data);
        console.log('[Home] products resp', p?.data);
        setTotals(t.data || {});
        setCryptoStats(c.data || { rows: [], totalBase: 0, count: 0 });
        setProductsStats((p.data && p.data.data) || []);
      } catch (e) {
        console.error('[Home] stats load error', e);
        setTotals({ totalSum: 0, totalBaseSum: 0, totalCuponSum: 0, ordersCount: 0, usersCount: 0 });
        setCryptoStats({ rows: [], totalBase: 0, count: 0 });
        setProductsStats([]);
      }
      setTotalsLoading(false);
      setCryptoLoading(false);
      setProductsLoading(false);
    };
    loadStats();
  }, [canAccess, merchantId, startDate, endDate]);

  // Load total payout across all merchants
  useEffect(() => {
    if (!canAccess || merchants.length === 0) return;
    const loadTotalPayout = async () => {
      setTotalPayoutLoading(true);
      try {
        const params = { startDate, endDate };
        console.log('[Home] Load total payout for merchants:', merchants.map(m => m._id || m.id));
        
        // Запрашиваем статистику по каждому мерчанту параллельно
        const promises = merchants.map(async (merchant) => {
          const merchantId = merchant._id || merchant.id;
          try {
            const merchantParams = { ...params, merchant_id: merchantId };
            const resp = await api.get('/dashboard/stats', { params: merchantParams });
            const payout = Number(resp.data?.totalBaseSum || 0);
            console.log(`[Home] Merchant ${merchantId} payout:`, payout);
            return {
              merchantId,
              merchantName: merchant.name || merchantId,
              payout,
              success: true,
              data: resp.data
            };
          } catch (e) {
            console.warn(`[Home] Merchant ${merchantId} error:`, e.message);
            return {
              merchantId,
              merchantName: merchant.name || merchantId,
              payout: 0,
              success: false,
              error: e.message
            };
          }
        });
        
        const results = await Promise.all(promises);
        const totalPayout = results.reduce((sum, result) => sum + result.payout, 0);
        
        console.log('[Home] Total payout calculation:', { totalPayout, results });
        
        setTotalPayout({
          totalPayout,
          merchantsCount: merchants.length,
          merchants: results
        });
      } catch (e) {
        console.error('[Home] total payout load error', e);
        setTotalPayout({ totalPayout: 0, merchantsCount: 0, merchants: [] });
      }
      setTotalPayoutLoading(false);
    };
    loadTotalPayout();
  }, [canAccess, merchants, startDate, endDate]);

  const reloadStats = async () => {
    if (!merchantId) return;
    setStatsLoading(true);
    try {
      const resp = await dashboardAPI.getCourierStats({ merchant_id: merchantId, startDate, endDate });
      setStats(resp.data?.data || resp.data);
    } catch (e) {
      setStats(null);
    }
    setStatsLoading(false);
  };

  const reloadSalaries = async () => {
    if (!merchantId) return;
    setSalariesLoading(true);
    try {
      const resp = await dashboardAPI.getCourierSalaries({ merchant_id: merchantId, startDate, endDate });
      setSalaries(resp.data?.data || resp.data);
    } catch (e) {
      setSalaries(null);
    }
    setSalariesLoading(false);
  };

  const openCourier = async (cId) => {
    setCourierId(cId);
    setCourierOpen(true);
    setCourierLoading(true);
    try {
      const resp = await dashboardAPI.getCourierById(cId, { merchant_id: merchantId, startDate, endDate, showNotFound: showNotFound ? 'true' : 'false' });
      setCourier(resp.data?.data || resp.data);
    } catch (e) {
      setCourier(null);
    }
    setCourierLoading(false);
  };

  const refreshCourier = async () => {
    if (!courierId) return;
    setCourierLoading(true);
    try {
      const resp = await dashboardAPI.getCourierById(courierId, { merchant_id: merchantId, startDate, endDate, showNotFound: showNotFound ? 'true' : 'false' });
      setCourier(resp.data?.data || resp.data);
    } catch (e) {
      // keep previous
    }
    setCourierLoading(false);
  };

  useEffect(() => {
    reloadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, startDate, endDate]);

  useEffect(() => {
    if (tab === 1) {
      reloadSalaries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, merchantId, startDate, endDate]);

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const exportSalariesCsv = () => {
    if (!salaries?.couriers) return;
    const rows = [];
    rows.push(['Курьер','Успешных','Ненаходы','Всего','Зарплата','Штрафы фасовки','Доп. штрафы','Общие штрафы','Итог ЗП','Выручка','% ЗП']);
    for (const c of salaries.couriers) {
      rows.push([
        c.courierName || c.courierTelegram || c.courierId,
        c.successfulOrders,
        c.notFoundOrders,
        c.totalOrders,
        c.totalSalary,
        c.packagingPenalty,
        c.additionalPenalty,
        c.totalPenalty,
        c.finalSalary,
        c.totalRevenue,
        typeof c.salaryPercentage === 'number' ? c.salaryPercentage.toFixed(2) : ''
      ]);
    }
    rows.push([]);
    rows.push(['ИТОГО','','','',
      '', '', '',
      salaries?.totalStats?.totalPenaltySum,
      salaries?.totalStats?.totalFinalSalarySum,
      salaries?.totalStats?.totalRevenueSum,
      typeof salaries?.totalStats?.avgSalaryPercentage === 'number' ? salaries.totalStats.avgSalaryPercentage.toFixed(2) : ''
    ]);
    const csv = rows.map(r => r.map(v => {
      const s = v == null ? '' : String(v);
      if (/[",;\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    }).join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'courier_salaries.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (canAccess === null) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!canAccess || !isAuth) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Нет доступа</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4, p: 2 }}>
      <Card elevation={3} sx={{ borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5">Дашборд</Typography>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="merchant-select-label">Мерчант</InputLabel>
              <Select
                labelId="merchant-select-label"
                value={merchantId}
                label="Мерчант"
                onChange={(e) => setMerchantId(e.target.value)}
                input={<OutlinedInput label="Мерчант" />}
              >
                {loadingMerchants && <MenuItem value=""><em>Загрузка...</em></MenuItem>}
                {!loadingMerchants && merchants.length === 0 && <MenuItem value=""><em>Нет доступных</em></MenuItem>}
                {merchants.map(m => (
                  <MenuItem key={m._id || m.id} value={m._id || m.id}>{m.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Начало"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="Конец"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Box>
        <Divider />
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
          <Tab label="Обзор" />
          <Tab label="Зарплаты" />
        </Tabs>
        <Divider />
        <CardContent>
          {statsLoading && (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          )}
          {!statsLoading && stats && tab === 0 && (
            <Box>
              {/* Header with refresh */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    Дашборд
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Период: {dayjs(startDate).format('DD.MM.YYYY')} — {dayjs(endDate).format('DD.MM.YYYY')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => window.location.reload()} size="small">
                    Обновить
                  </Button>
                  <Button variant="contained" color="secondary" onClick={() => setTab(1)} startIcon={<PersonIcon />} size="small">
                    Зарплаты
                  </Button>
                </Box>
              </Box>

              {/* Total Payout Block */}
              <Card sx={{ 
                background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                mb: 3,
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 25px ${alpha(theme.palette.error.main, 0.15)}` }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <WalletIcon sx={{ color: theme.palette.error.main, mr: 2, fontSize: 32 }} />
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                          Всего к выплате
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          По всем мерчантам ({totalPayout.merchantsCount} шт.)
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      {totalPayoutLoading ? (
                        <CircularProgress size={24} sx={{ color: theme.palette.error.main }} />
                      ) : (
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                          {formatCurrency(totalPayout.totalPayout || 0)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Top KPI cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { title: 'Общая сумма', value: formatCurrency(totals?.totalSum || 0), icon: MoneyIcon, color: theme.palette.primary.main, loading: totalsLoading },
                  { title: 'К выплате', value: formatCurrency(totals?.totalBaseSum || 0), icon: WalletIcon, color: theme.palette.success.main, loading: totalsLoading },
                  { title: 'Купоны', value: formatCurrency(totals?.totalCuponSum || 0), icon: CouponIcon, color: theme.palette.warning.main, loading: totalsLoading },
                  { title: 'Пользователи', value: totals?.usersCount || 0, icon: UsersIcon, color: theme.palette.info.main, loading: totalsLoading },
                  { title: 'Продажи', value: totals?.ordersCount || 0, icon: OrdersIcon, color: theme.palette.secondary.main, loading: totalsLoading },
                ].map((kpi, idx) => (
                  <Grid item xs={6} sm={4} md key={idx}>
                    <Card sx={{ 
                      background: `linear-gradient(135deg, ${alpha(kpi.color, 0.1)} 0%, ${alpha(kpi.color, 0.05)} 100%)`,
                      border: `1px solid ${alpha(kpi.color, 0.2)}`,
                      transition: 'all 0.2s ease',
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 4px 20px ${alpha(kpi.color, 0.2)}` }
                    }}>
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <kpi.icon sx={{ color: kpi.color, mr: 1, fontSize: 20 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                            {kpi.title}
                          </Typography>
                        </Box>
                        {kpi.loading ? (
                          <CircularProgress size={16} sx={{ color: kpi.color }} />
                        ) : (
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: kpi.color }}>
                            {kpi.value}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Crypto & Products row */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Crypto summary */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                    height: '100%',
                    transition: 'all 0.2s ease',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 25px ${alpha(theme.palette.warning.main, 0.15)}` }
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CryptoIcon sx={{ color: theme.palette.warning.main, mr: 1.5, fontSize: 28 }} />
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Оплаты криптой</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Сумма к выплате: <strong>{formatCurrency(cryptoStats?.totalBase || 0)}</strong>
                          </Typography>
                        </Box>
                        {cryptoLoading && <CircularProgress size={20} sx={{ ml: 'auto', color: theme.palette.warning.main }} />}
                      </Box>
                      
                      <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                        {(cryptoStats?.rows || []).map((row, idx) => (
                          <Box key={row.platform} sx={{ 
                            p: 2, 
                            mb: 1,
                            bgcolor: alpha(theme.palette.warning.main, 0.08),
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}`,
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'all 0.15s ease',
                            '&:hover': { 
                              bgcolor: alpha(theme.palette.warning.main, 0.12),
                              transform: 'translateX(4px)'
                            }
                          }}>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.warning.dark }}>
                                {row.platform}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.count} транзакций
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                {formatCurrency(row.totalBase)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                        {(!cryptoStats?.rows || cryptoStats.rows.length === 0) && !cryptoLoading && (
                          <Box sx={{ 
                            p: 4, 
                            textAlign: 'center',
                            bgcolor: alpha(theme.palette.warning.main, 0.05),
                            borderRadius: 2,
                            border: `2px dashed ${alpha(theme.palette.warning.main, 0.2)}`
                          }}>
                            <CryptoIcon sx={{ fontSize: 48, color: alpha(theme.palette.warning.main, 0.3), mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              Нет данных об оплатах криптой
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Sold positions (products) */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    height: '100%',
                    transition: 'all 0.2s ease',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 25px ${alpha(theme.palette.success.main, 0.15)}` }
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ProductsIcon sx={{ color: theme.palette.success.main, mr: 1.5, fontSize: 28 }} />
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Проданные позиции</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Топ товаров за период
                          </Typography>
                        </Box>
                        {productsLoading && <CircularProgress size={20} sx={{ ml: 'auto', color: theme.palette.success.main }} />}
                      </Box>
                      
                      <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                        {(productsStats || []).slice(0, 6).map((p, idx) => (
                          <Box key={p._id?.product || p.productName} sx={{ 
                            p: 2, 
                            mb: 1,
                            bgcolor: alpha(theme.palette.success.main, 0.08),
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'all 0.15s ease',
                            '&:hover': { 
                              bgcolor: alpha(theme.palette.success.main, 0.12),
                              transform: 'translateX(4px)'
                            }
                          }}>
                            <Box sx={{ flex: 1, mr: 2 }}>
                              <Typography variant="subtitle2" sx={{ 
                                fontWeight: 700, 
                                color: theme.palette.success.dark,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {p.productName || 'Товар'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {p.totalItems || 0} продаж
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                {formatCurrency(p.totalAmount || 0)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                        {(productsStats || []).length > 6 && (
                          <Box sx={{ 
                            p: 1, 
                            textAlign: 'center',
                            bgcolor: alpha(theme.palette.success.main, 0.05),
                            borderRadius: 1,
                            border: `1px dashed ${alpha(theme.palette.success.main, 0.2)}`
                          }}>
                            <Typography variant="caption" color="text.secondary">
                              +{productsStats.length - 6} товаров еще...
                            </Typography>
                          </Box>
                        )}
                        {(!productsStats || productsStats.length === 0) && !productsLoading && (
                          <Box sx={{ 
                            p: 4, 
                            textAlign: 'center',
                            bgcolor: alpha(theme.palette.success.main, 0.05),
                            borderRadius: 2,
                            border: `2px dashed ${alpha(theme.palette.success.main, 0.2)}`
                          }}>
                            <ProductsIcon sx={{ fontSize: 48, color: alpha(theme.palette.success.main, 0.3), mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              Нет данных о продажах
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Статистика курьеров</Typography>
                <Button variant="contained" color="secondary" onClick={() => setTab(1)} startIcon={<PersonIcon />}>Зарплаты</Button>
              </Box>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Период: {stats?.period?.startDate || startDate} — {stats?.period?.endDate || endDate}
              </Typography>
              {/* KPI cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CancelIcon color="error" sx={{ mr: 1 }} />
                      <Typography variant="h6">{stats?.totalStats?.totalNotFound ?? 0}</Typography>
                    </Box>
                    <Typography color="text.secondary">Ненаходы (курьеры)</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CancelIcon color="warning" sx={{ mr: 1 }} />
                      <Typography variant="h6">{stats?.totalStats?.totalNotFoundPeriod ?? 0}</Typography>
                    </Box>
                    <Typography color="text.secondary">Всего ненаходов за период</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">{stats?.totalStats?.totalCouriers ?? 0}</Typography>
                    </Box>
                    <Typography color="text.secondary">Всего курьеров</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AssignmentIcon sx={{ mr: 1, color: 'info.main' }} />
                      <Typography variant="h6">{stats?.totalStats?.totalOrders ?? 0}</Typography>
                    </Box>
                    <Typography color="text.secondary">Всего заказов</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
                      <Typography variant="h6">{stats?.totalStats?.overallSuccessRate ?? 0}%</Typography>
                    </Box>
                    <Typography color="text.secondary">Общий успех</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card><CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TrendingUpIcon sx={{ mr: 1, color: 'success.main' }} />
                      <Typography variant="h6">{formatCurrency(stats?.totalStats?.totalRevenue ?? 0)}</Typography>
                    </Box>
                    <Typography color="text.secondary">Общая выручка</Typography>
                  </CardContent></Card>
                </Grid>
              </Grid>

              {/* Couriers table */}
              <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <Typography variant="h6" sx={{ p: 2 }}>Детальная статистика по курьерам</Typography>
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Курьер</TableCell>
                        <TableCell align="right">Заказов</TableCell>
                        <TableCell align="right">Успешно</TableCell>
                        <TableCell align="right">Ненаходы</TableCell>
                        <TableCell align="right">% Успеха</TableCell>
                        <TableCell align="right">Выручка</TableCell>
                        <TableCell align="right">Средний чек</TableCell>
                        <TableCell align="center">Действия</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(stats?.couriers || []).map((c) => {
                        const rate = typeof c.successRate === 'number' ? c.successRate : (c.totalOrders ? Math.round((c.successfulOrders / c.totalOrders) * 10000) / 100 : 0);
                        const rateColor = rate >= 90 ? 'success' : rate >= 80 ? 'warning' : 'error';
                        return (
                          <TableRow key={c.courierId} hover>
                            <TableCell>
                              <Typography variant="subtitle2">{c.courierName || c.courierUsername || c.courierId}</Typography>
                              {c.courierTelegram && (
                                <Typography variant="caption" color="text.secondary">TG: {c.courierTelegram}</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right"><Typography variant="h6">{c.totalOrders || 0}</Typography></TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <CheckCircleIcon sx={{ mr: 0.5, color: 'success.main', fontSize: 16 }} />{c.successfulOrders || 0}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <CancelIcon sx={{ mr: 0.5, color: 'error.main', fontSize: 16 }} />{c.notFoundOrders || 0}
                              </Box>
                            </TableCell>
                            <TableCell align="right"><Chip size="small" label={`${rate}%`} color={rateColor} /></TableCell>
                            <TableCell align="right"><Typography fontWeight="bold">{formatCurrency(c.totalRevenue || 0)}</Typography></TableCell>
                            <TableCell align="right">{formatCurrency(c.avgOrderValue || 0)}</TableCell>
                            <TableCell align="center">
                              <Tooltip title="Подробная статистика">
                                <IconButton size="small" onClick={() => openCourier(c.courierId)}>
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {(!stats?.couriers || stats.couriers.length === 0) && (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">Нет данных для отображения</Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
          {tab === 1 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Период: {startDate} — {endDate}
              </Typography>
              {salariesLoading && (
                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
              )}
              {!salariesLoading && salaries && (
                <Box>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={3}><StatCard title="Всего курьеров" value={salaries?.totalStats?.totalCouriers} /></Grid>
                    <Grid item xs={12} md={3}><StatCard title="Сумма зарплат" value={formatCurrency(salaries?.totalStats?.totalSalarySum)} /></Grid>
                    <Grid item xs={12} md={3}><StatCard title="Сумма штрафов" value={formatCurrency(salaries?.totalStats?.totalPenaltySum)} /></Grid>
                    <Grid item xs={12} md={3}><StatCard title="Итого к выплате" value={formatCurrency(salaries?.totalStats?.totalFinalSalarySum)} /></Grid>
                  </Grid>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportSalariesXlsx}>
                      Экспорт Excel
                    </Button>
                  </Box>
                  <TableContainer component={Paper} sx={{ maxHeight: 560 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Курьер</TableCell>
                          <TableCell align="right">Заказы</TableCell>
                          <TableCell align="right">Успешно</TableCell>
                          <TableCell align="right">Ненаходы</TableCell>
                          <TableCell align="right">Зарплата</TableCell>
                          <TableCell align="right">Штрафы (уп.)</TableCell>
                          <TableCell align="right">Доп. штрафы</TableCell>
                          <TableCell align="right">Итого</TableCell>
                          <TableCell align="center">Действия</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(salaries?.couriers || []).map((c) => (
                          <React.Fragment key={c.courierId}>
                            <TableRow hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{c.courierName || c.courierTelegram || c.courierId}</Typography>
                              </TableCell>
                              <TableCell align="right">{c.totalOrders}</TableCell>
                              <TableCell align="right">{c.successfulOrders}</TableCell>
                              <TableCell align="right">{c.notFoundOrders}</TableCell>
                              <TableCell align="right">{formatCurrency(c.totalSalary)}</TableCell>
                              <TableCell align="right">{formatCurrency(c.packagingPenalty)}</TableCell>
                              <TableCell align="right">{formatCurrency(c.additionalPenalty)}</TableCell>
                              <TableCell align="right">{formatCurrency(c.finalSalary)}</TableCell>
                              <TableCell align="center">
                                <Stack direction="row" spacing={1} justifyContent="center">
                                  <Tooltip title="Детали курьера">
                                    <IconButton size="small" onClick={() => openCourier(c.courierId)}>
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={expanded.has(c.courierId) ? 'Скрыть фасовки' : 'Показать фасовки'}>
                                    <IconButton size="small" onClick={() => toggleExpanded(c.courierId)}>
                                      {expanded.has(c.courierId) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                            {expanded.has(c.courierId) && Array.isArray(c.packagingDetails) && c.packagingDetails.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={9} sx={{ bgcolor: 'grey.50', p: 2 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Детализация по фасовкам — {c.courierName || c.courierId}</Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Фасовка</TableCell>
                                        <TableCell align="right">Адресов</TableCell>
                                        <TableCell align="right">ЗП за адрес</TableCell>
                                        <TableCell align="right">Общая ЗП</TableCell>
                                        <TableCell align="right">Заказов</TableCell>
                                        <TableCell align="right">Успешных</TableCell>
                                        <TableCell align="right">Ненаходов</TableCell>
                                        <TableCell align="right">Штраф за ненаход</TableCell>
                                        <TableCell align="right">Общий штраф</TableCell>
                                        <TableCell align="right">% успешности</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {c.packagingDetails.map((p, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell>{p.packagingName}</TableCell>
                                          <TableCell align="right">{p.addressCount}</TableCell>
                                          <TableCell align="right">{formatCurrency(p.paymentPerAddress)}</TableCell>
                                          <TableCell align="right">{formatCurrency(p.totalPayment)}</TableCell>
                                          <TableCell align="right">{p.totalOrders}</TableCell>
                                          <TableCell align="right">{p.successfulOrders}</TableCell>
                                          <TableCell align="right">{p.notFoundOrders}</TableCell>
                                          <TableCell align="right">{formatCurrency(p.penaltyPerOrder)}</TableCell>
                                          <TableCell align="right">{formatCurrency(p.totalPenalty)}</TableCell>
                                          <TableCell align="right">{typeof p.successRate === 'number' ? `${p.successRate.toFixed(1)}%` : ''}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
          {!statsLoading && !stats && (
            <Typography color="text.secondary">Выберите мерчанта и период для загрузки статистики.</Typography>
          )}
        </CardContent>
      </Card>

      {/* Courier Detail Dialog */}
      <Dialog open={courierOpen} onClose={() => setCourierOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Информация по курьеру</DialogTitle>
        <DialogContent dividers>
          {courierLoading && (<Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>)}
          {!courierLoading && courier && (
            <Box>
              {/* Filters */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Фильтры</Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Дата начала"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Дата окончания"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button variant="contained" fullWidth disabled={courierLoading} onClick={refreshCourier}>Применить</Button>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button variant="outlined" fullWidth disabled={courierLoading} onClick={() => { setStartDate(''); setEndDate(''); setTimeout(refreshCourier, 50); }}>Сбросить</Button>
                  </Grid>
                </Grid>
              </Paper>

              {/* Courier info */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Информация о курьере</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Имя:</Typography>
                    <Typography variant="body1" fontWeight="bold">{courier?.courier?.name || courier?.courier?.id}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Username:</Typography>
                    <Typography variant="body1">{courier?.courier?.username ? `@${courier.courier.username}` : '-'}</Typography>
                  </Grid>
                  {courier?.courier?.telegram && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">Telegram:</Typography>
                      <Typography variant="body1">{courier.courier.telegram}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* KPIs */}
              {courier?.summary && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card><CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AssignmentIcon color="primary" sx={{ mr: 1 }} />
                        <Box>
                          <Typography color="textSecondary" gutterBottom>Всего заказов</Typography>
                          <Typography variant="h5">{courier.summary.totalOrders}</Typography>
                        </Box>
                      </Box>
                    </CardContent></Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card><CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
                        <Typography variant="h6">{courier.summary.successRate ?? Math.round((courier.summary.successfulOrders / Math.max(1, courier.summary.totalOrders)) * 100)}</Typography>
                      </Box>
                      <Typography color="text.secondary">Процент успеха</Typography>
                    </CardContent></Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card><CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CancelIcon sx={{ mr: 1, color: 'error.main' }} />
                        <Typography variant="h6">{courier.summary.notFoundOrders}</Typography>
                      </Box>
                      <Typography color="text.secondary">Ненаходы</Typography>
                    </CardContent></Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card><CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TrendingUpIcon sx={{ mr: 1, color: 'success.main' }} />
                        <Typography variant="h6">{formatCurrency(courier.summary.totalRevenue)}</Typography>
                      </Box>
                      <Typography color="text.secondary">Общая выручка</Typography>
                    </CardContent></Card>
                  </Grid>
                </Grid>
              )}

              {/* Orders table with toggle */}
              <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">{showNotFound ? 'Все ненаходы' : 'Последние заказы'}</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant={!showNotFound ? 'contained' : 'outlined'} size="small" onClick={() => { setShowNotFound(false); }} disabled={courierLoading}>Последние заказы</Button>
                    <Button variant={showNotFound ? 'contained' : 'outlined'} size="small" color="error" onClick={() => { setShowNotFound(true); }} disabled={courierLoading}>Показать ненаходы</Button>
                  </Box>
                </Box>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID заказа</TableCell>
                        <TableCell>Дата</TableCell>
                        <TableCell>Статус</TableCell>
                        <TableCell>Город</TableCell>
                        <TableCell>Район</TableCell>
                        <TableCell>Товар</TableCell>
                        <TableCell>Тип</TableCell>
                        <TableCell align="right">Сумма</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(courier?.recentOrders || []).filter(o => showNotFound ? o.not_found : !o.not_found).map((o, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell><Typography variant="body2" fontWeight={600}>{o.orderId || '-'}</Typography></TableCell>
                          <TableCell>{o.date ? String(o.date).slice(0, 16).replace('T', ' ') : ''}</TableCell>
                          <TableCell>
                            <Chip size="small" label={o.status === 'successful' ? 'Успешно' : 'Ненаход'} color={o.status === 'successful' ? 'success' : 'error'} />
                          </TableCell>
                          <TableCell>{pickFirst(o.city, o.cityName, o.town, o.locality, o?.address?.city, o?.location?.city, o?.region?.city)}</TableCell>
                          <TableCell>{pickFirst(o.district, o.districtName, o.area, o.region, o?.address?.district, o?.location?.district, o?.region?.district)}</TableCell>
                          <TableCell>{o.product || '-'}</TableCell>
                          <TableCell>{o.type || '-'}</TableCell>
                          <TableCell align="right"><Typography fontWeight={600}>{formatCurrency(o.revenue)}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {(!courier?.recentOrders || courier.recentOrders.length === 0) && (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">Нет заказов для отображения</Typography>
                  </Box>
                )}
              </Paper>

              <Typography variant="subtitle2" sx={{ mt: 3 }}>Штрафы</Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Сумма</TableCell>
                    <TableCell>Комментарий</TableCell>
                    <TableCell>Дата</TableCell>
                    <TableCell>Статус</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(courier?.additionalPenalties || []).map((p, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{p.amount}</TableCell>
                      <TableCell>{p.comment}</TableCell>
                      <TableCell>{p.penaltyDate ? String(p.penaltyDate).slice(0, 10) : ''}</TableCell>
                      <TableCell>{p.isActive ? 'Активен' : 'Отменен'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowNotFound(!showNotFound); setTimeout(refreshCourier, 0); }}>
            {showNotFound ? 'Скрыть все ненаходы' : 'Показать все ненаходы'}
          </Button>
          <Button onClick={() => setCourierOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const StatCard = ({ title, value }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
      <Typography variant="h5" sx={{ mt: 1 }}>{value ?? '—'}</Typography>
    </CardContent>
  </Card>
);

export default Home;
